import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

/**
 * Hook for managing URL search params as state
 * Allows components to read/write state that persists in the URL
 */
export const useUrlState = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Get a single param value from URL
   */
  const getParam = useCallback((key: string): string | null => {
    return searchParams.get(key);
  }, [searchParams]);

  /**
   * Set a single param in URL (merges with existing params)
   */
  const setParam = useCallback((key: string, value: string | null, options?: { replace?: boolean }) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === null || value === undefined || value === '') {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    setSearchParams(newParams, { replace: options?.replace ?? true });
  }, [searchParams, setSearchParams]);

  /**
   * Set multiple params at once (merges with existing params)
   */
  const setParams = useCallback((params: Record<string, string | null>, options?: { replace?: boolean }) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    setSearchParams(newParams, { replace: options?.replace ?? true });
  }, [searchParams, setSearchParams]);

  /**
   * Clear specific params from URL
   */
  const clearParams = useCallback((keys: string[], options?: { replace?: boolean }) => {
    const newParams = new URLSearchParams(searchParams);
    keys.forEach(key => newParams.delete(key));
    setSearchParams(newParams, { replace: options?.replace ?? true });
  }, [searchParams, setSearchParams]);

  /**
   * Clear all params from URL
   */
  const clearAllParams = useCallback((options?: { replace?: boolean }) => {
    setSearchParams(new URLSearchParams(), { replace: options?.replace ?? true });
  }, [setSearchParams]);

  /**
   * Navigate to a path while preserving current search params
   */
  const navigateWithParams = useCallback((path: string, additionalParams?: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });
    }
    const queryString = newParams.toString();
    navigate(queryString ? `${path}?${queryString}` : path);
  }, [navigate, searchParams]);

  /**
   * Get all current params as an object
   */
  const allParams = useMemo(() => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [searchParams]);

  return {
    getParam,
    setParam,
    setParams,
    clearParams,
    clearAllParams,
    navigateWithParams,
    allParams,
    searchParams,
    location,
  };
};

/**
 * Helper hook for typed URL state with default values
 * Useful for components that need a specific param with type safety
 */
export function useUrlParam<T extends string>(
  key: string,
  defaultValue: T
): [T, (value: T | null) => void] {
  const { getParam, setParam } = useUrlState();
  
  const value = (getParam(key) as T) ?? defaultValue;
  
  const setValue = useCallback((newValue: T | null) => {
    // If setting to default value, remove from URL to keep it clean
    if (newValue === defaultValue) {
      setParam(key, null);
    } else {
      setParam(key, newValue);
    }
  }, [key, defaultValue, setParam]);

  return [value, setValue];
}

/**
 * Helper hook for boolean URL params
 */
export function useUrlBooleanParam(
  key: string,
  defaultValue: boolean = false
): [boolean, (value: boolean) => void] {
  const { getParam, setParam } = useUrlState();
  
  const rawValue = getParam(key);
  const value = rawValue === null ? defaultValue : rawValue === 'true';
  
  const setValue = useCallback((newValue: boolean) => {
    // If setting to default value, remove from URL to keep it clean
    if (newValue === defaultValue) {
      setParam(key, null);
    } else {
      setParam(key, String(newValue));
    }
  }, [key, defaultValue, setParam]);

  return [value, setValue];
}

